/**
 * Every call the saved-words feature makes, and the dispatches that follow.
 *
 * IO stays out of the store, matching services/auth-log.ts and
 * services/user.service.ts. Writes are optimistic: the heart flips immediately
 * and rolls back if the request fails, because a save is a one-word change the
 * user is watching and a spinner on it would be worse than a rare undo.
 */

import { authedFetch, type ApiResult } from 'api/authed-fetch'
import { favoriteKey, favoritesActions, type FavoriteKey } from 'store/favorites.reducer'
import type {
   MeResponse,
   SavedFavorite,
   SavedLang,
   SavedTag,
} from '../../shared/favorites-contract'
import type { NormalizedTag } from 'lib/tag-name'

type Dispatch = (action: { type: string; payload?: unknown }) => unknown

const call = <T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> =>
   authedFetch<T>(path, init, 'favorites')

const wordQuery = (lang: SavedLang, word: string) =>
   `lang=${encodeURIComponent(lang)}&word=${encodeURIComponent(word)}`

export async function loadFavorites(dispatch: Dispatch): Promise<void> {
   dispatch(favoritesActions.loadStarted())
   const result = await call<MeResponse>('/api/me')
   if (!result.ok) {
      dispatch(favoritesActions.loadFailed())
      return
   }
   dispatch(favoritesActions.loadSucceeded(result.data))
}

/**
 * Toggles one word, optimistically.
 *
 * `previous` is the row as it was, so a failure restores it exactly — including
 * its tag ids, which an unsave would otherwise drop on the floor.
 */
export async function toggleFavorite(
   dispatch: Dispatch,
   lang: SavedLang,
   word: string,
   previous: SavedFavorite | null
): Promise<void> {
   const key = favoriteKey(lang, word)
   dispatch(favoritesActions.writeStarted(key))

   if (previous) {
      dispatch(favoritesActions.optimisticRemoved(key))
      const result = await call<{ ok: true }>(`/api/favorites?${wordQuery(lang, word)}`, {
         method: 'DELETE',
      })
      if (result.ok) dispatch(favoritesActions.writeRemoved(key))
      else dispatch(favoritesActions.writeRolledBack({ key, previous }))
      return
   }

   // Stands in until the server answers with the real id and snapshot. Shown
   // for a few hundred milliseconds at most, and only on the button's own
   // pressed state — the list page's row text comes from the confirmed row.
   dispatch(
      favoritesActions.optimisticAdded({
         key,
         favorite: {
            id: -1,
            lang,
            word,
            t2e: '',
            meaning: '',
            createdAt: new Date().toISOString(),
            tagIds: [],
         },
      })
   )

   const result = await call<{ favorite: SavedFavorite }>('/api/favorites', {
      method: 'POST',
      body: JSON.stringify({ lang, word }),
   })
   if (result.ok) dispatch(favoritesActions.writeConfirmed({ key, favorite: result.data.favorite }))
   else dispatch(favoritesActions.writeRolledBack({ key, previous: null }))
}

export async function setFavoriteTags(
   dispatch: Dispatch,
   lang: SavedLang,
   word: string,
   tagIds: number[],
   newTagNames: string[]
): Promise<boolean> {
   const result = await call<{ tagIds: number[]; tags: SavedTag[] }>('/api/favorites/tags', {
      method: 'PUT',
      body: JSON.stringify({ lang, word, tagIds, newTagNames }),
   })
   if (!result.ok) return false

   dispatch(
      favoritesActions.tagsReplaced({
         key: favoriteKey(lang, word),
         tagIds: result.data.tagIds,
         tags: result.data.tags,
      })
   )
   return true
}

export async function createTag(dispatch: Dispatch, tag: NormalizedTag): Promise<SavedTag | null> {
   const result = await call<{ tag: SavedTag }>('/api/tags', {
      method: 'POST',
      body: JSON.stringify({ name: tag.name }),
   })
   if (!result.ok) return null

   dispatch(favoritesActions.tagUpserted(result.data.tag))
   return result.data.tag
}

export type RenameOutcome =
   | { outcome: 'renamed'; tag: SavedTag }
   /** The name belongs to another tag; the UI offers to merge into it. */
   | { outcome: 'conflict'; tagId: number }
   | { outcome: 'failed' }

export async function renameTag(
   dispatch: Dispatch,
   id: number,
   tag: NormalizedTag
): Promise<RenameOutcome> {
   const result = await call<{ tag: SavedTag }>('/api/tags', {
      method: 'PATCH',
      body: JSON.stringify({ id, name: tag.name }),
   })

   if (result.ok) {
      dispatch(favoritesActions.tagUpserted(result.data.tag))
      return { outcome: 'renamed', tag: result.data.tag }
   }

   // The colliding tag's id rides along on the 409, so the caller can turn a
   // dead end into "Merge into 'Food'?" without another round trip.
   if (result.error === 'tag_exists' && typeof result.body.tagId === 'number') {
      return { outcome: 'conflict', tagId: result.body.tagId }
   }
   return { outcome: 'failed' }
}

export async function deleteTag(
   dispatch: Dispatch,
   id: number,
   mergeInto: number | null
): Promise<boolean> {
   const query = mergeInto === null ? `id=${id}` : `id=${id}&mergeInto=${mergeInto}`
   const result = await call<{ ok: true }>(`/api/tags?${query}`, { method: 'DELETE' })
   if (!result.ok) return false

   dispatch(favoritesActions.tagRemoved({ id, mergedInto: mergeInto }))
   return true
}

/** Keys whose word the dictionary no longer returns. */
export function markDangling(dispatch: Dispatch, keys: FavoriteKey[]): void {
   if (keys.length) dispatch(favoritesActions.markedDangling(keys))
}
